/**
 * statementScan — token-aware scans shared by the DML outline extractors
 * (UPDATE / DELETE / INSERT): a qualified target name, the first top-level
 * keyword out of a set, whether a span introduces another relation (JOIN or
 * comma), and whitespace trimming. Built on the shared primitives in
 * `insert/insertParseHelpers.ts`; before this module each extractor carried
 * its own identical copy.
 */
import type { SkipContext } from '../../../../sql-resolver/classify/tokenSkip';
import { trySkipNonCode } from '../../../../sql-resolver/classify/tokenSkip';
import { IDENT_CONT, IDENT_START, isWordBoundaryBefore, readIdentifier } from '../insert/insertParseHelpers';

export interface TargetMatch {
  readonly text: string;
  readonly start: number;
  readonly end: number;
}

export function readQualifiedTarget(source: string, from: number, end: number): TargetMatch | null {
  const start = from;
  const first = readIdentifier(source, from, end);
  if (!first) return null;
  let i = first.end;
  while (i < end && source[i] === '.') {
    const next = readIdentifier(source, i + 1, end);
    if (!next) break;
    i = next.end;
  }
  return { text: source.slice(start, i), start, end: i };
}

export interface KeywordHit {
  readonly upper: string;
  readonly start: number;
  readonly end: number;
}

export function findTopLevelKeyword(
  source: string,
  from: number,
  end: number,
  ctx: SkipContext,
  targets: readonly string[],
): KeywordHit | null {
  let i = from;
  let paren = 0;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    const c = source[i];
    if (c === '(') { paren++; i++; continue; }
    if (c === ')') { if (paren > 0) paren--; i++; continue; }
    if (paren === 0 && IDENT_START.test(c) && isWordBoundaryBefore(source, i)) {
      const wStart = i;
      let j = i + 1;
      while (j < end && IDENT_CONT.test(source[j])) j++;
      const word = source.slice(wStart, j).toUpperCase();
      if (targets.includes(word)) {
        return { upper: word, start: wStart, end: j };
      }
      i = j;
      continue;
    }
    i++;
  }
  return null;
}

/**
 * True when `[start,end)` contains a top-level `JOIN` keyword or a top-level
 * comma — i.e. the FROM / multi-table tail introduces a relation beyond the
 * target (a real join / cross-join), as opposed to a bare trailing alias or an
 * MSSQL self-FROM naming only the target. Quoting / comments / parens are
 * invisible to the scan (token-aware). Used for `hasJoinTail` (I8).
 */
export function spanHasJoinOrComma(source: string, start: number, end: number, ctx: SkipContext): boolean {
  let i = start;
  let paren = 0;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    const c = source[i];
    if (c === '(') { paren++; i++; continue; }
    if (c === ')') { if (paren > 0) paren--; i++; continue; }
    if (paren === 0 && c === ',') return true;
    if (paren === 0 && IDENT_START.test(c) && isWordBoundaryBefore(source, i)) {
      let j = i + 1;
      while (j < end && IDENT_CONT.test(source[j])) j++;
      if (source.slice(i, j).toUpperCase() === 'JOIN') return true;
      i = j;
      continue;
    }
    i++;
  }
  return false;
}

export interface TrimmedSpan {
  readonly start: number;
  readonly end: number;
}

export function trimSpan(source: string, start: number, end: number): TrimmedSpan | null {
  let s = start;
  while (s < end && /\s/.test(source[s])) s++;
  let e = end;
  while (e > s && /\s/.test(source[e - 1])) e--;
  if (e <= s) return null;
  return { start: s, end: e };
}
